/**
 * One camera, from "the user tapped play" to a track on screen.
 *
 * The order is fixed by the protocol and each step is a place it can stop:
 *
 *   mint token → open socket + register → connect → wait for invite → answer → ICE
 *
 * ## The two traps this file exists to not fall into
 *
 * **Candidates arrive before the offer.** The device starts trickling the moment it is
 * asked, without waiting for our answer. `addIceCandidate` before
 * `setRemoteDescription` throws, and a client that lets it throw ends up connecting on
 * only the candidates that happened to arrive late — which works on a flat LAN and
 * fails on the first real NAT. So every inbound candidate goes into `pendingIce` until
 * the remote description is set, and is flushed immediately after (cameras/live-video.md §1.2).
 *
 * **`peer` is the box, `profile` is the camera.** Passing a camera's `deviceId` as the
 * peer answers `480`, which is the same code an unplugged camera gives — the single
 * most expensive mistake on this API, because it looks like a hardware problem. The
 * peer comes off the token (`StreamToken.peer`), never off the camera row, so the two
 * cannot be confused here at all.
 *
 * ## What it deliberately does NOT do
 *
 * No `createOffer`. The device offers; we answer. See `signaling.ts`.
 */
import {useCallback, useEffect, useRef, useState} from 'react';

import {phaseAfterAnswer} from './phase';
import {constrainBaselineH264} from './sdp';
import {summarizeVideoStats, type VideoStats} from './video-stats';
import {mintStreamToken, reportIceOutcome, type IceOutcome} from '@/entities/stream/api';
import {attachPtzChannel, PTZ_CHANNEL_LABEL, refreshPtzChannels} from './ptz-channel';
import type {StreamCamera} from '@/entities/stream/types';
import {streamSocketUrl} from '@/shared/config/stream';
import {
  SignalingError,
  SignalingSocket,
  SIGNALING_CODES,
  type CandidateInit,
} from './signaling';
import {
  createPeerConnection,
  iceCandidate,
  sessionDescription,
  streamFromTrack,
  WEBRTC_AVAILABLE,
  type PeerConnection,
  type RemoteStream,
} from './webrtc';

/** Which rung of the ladder above we are on — the progress line shown while waiting. */
export type LivePhase = 'idle' | 'token' | 'socket' | 'offer' | 'ice' | 'playing' | 'error';

export type LiveState = {
  phase: LivePhase;
  stream: RemoteStream | null;
  /** i18n key under `live.error.*`. Set only in the `error` phase. */
  errorKey: string | null;
  /** The signalling code behind `errorKey`, for the diagnostics line. */
  errorCode: number | null;
  /** `direct` | `relay` once ICE settles — the same value reported to the server. */
  route: IceOutcome | null;
  /** Inbound video counters once a peer connection exists — see `video-stats.ts`. */
  video: VideoStats | null;
  /** Raw `iceConnectionState`, for the diagnostics line. */
  ice: string | null;
  /** Candidate traffic, for the diagnostics line — see `CandidateTally`. */
  tally: CandidateTally | null;
};

/**
 * What crossed the signalling socket, by candidate type. "Connected but nothing
 * happens" with ICE stuck in `new` is either the camera sending no candidates, every
 * one of them being rejected by `addIceCandidate`, or ours never leaving — these
 * counts tell which, on the device where it fails.
 */
export type CandidateTally = {
  /** Received from the camera, by `typ` (`host`, `srflx`, `relay`…). */
  rx: Record<string, number>;
  /** Received but refused by `addIceCandidate`. */
  rxRejected: number;
  /** Accepted by `addIceCandidate`. */
  rxAdded: number;
  /** The first refusal's message. */
  rxError: string | null;
  /** Sent to the camera, by `typ`. */
  tx: Record<string, number>;
  /** `iceGatheringState` at the last poll. */
  gathering: string | null;
  /** The first candidate the camera sent, as it came — format problems show here. */
  firstRx: string | null;
  /** The camera's offer in one line: m-sections (`kind:mid`), BUNDLE, ice-lite. */
  offer: string | null;
  /** What the browser's ICE agent itself knows, from getStats. */
  agent: string | null;
  /** Our answer's video section: port (0 = rejected), direction, codecs. */
  answer: string | null;
};

/**
 * The video section of an SDP in one line: `port 9 · recvonly · H264/96 VP8/97`.
 * Port 0 in an answer means the section was rejected — with BUNDLE that takes the
 * transport the camera's candidates name with it, and ICE never starts.
 */
export function summarizeVideoSection(sdp: string): string {
  const lines = sdp.split(/\r?\n/);
  const start = lines.findIndex(l => l.startsWith('m=video'));
  if (start < 0) {
    return 'no video section';
  }
  const end = lines.findIndex((l, i) => i > start && l.startsWith('m='));
  const section = lines.slice(start, end < 0 ? undefined : end);
  const port = section[0].split(' ')[1];
  const dir =
    section.find(l => /^a=(sendrecv|sendonly|recvonly|inactive)$/.test(l))?.slice(2) ?? '?';
  const codecs = section
    .filter(l => l.startsWith('a=rtpmap:'))
    .map(l => {
      const [pt, rest] = l.slice(9).split(' ');
      return `${rest.split('/')[0]}/${pt}`;
    })
    .filter(c => !/^(rtx|red|ulpfec|flexfec)/i.test(c));
  const profiles = section
    .filter(l => l.startsWith('a=fmtp:') && l.includes('profile-level-id'))
    .map(l => /profile-level-id=([0-9a-fA-F]{6})/.exec(l)?.[1])
    .filter(Boolean);
  return [
    `port ${port}`,
    dir,
    codecs.join(' ') || 'no codecs',
    profiles.length ? `plid ${profiles.join(',')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** `video:0 audio:1 · bundle 0 1 · ufrag 2 · lite` — enough to see a mid mismatch. */
function summarizeOffer(sdp: string): string {
  const lines = sdp.split(/\r?\n/);
  const sections: string[] = [];
  let kind: string | null = null;
  for (const l of lines) {
    if (l.startsWith('m=')) {
      if (kind) {
        sections.push(`${kind}:?`);
      }
      kind = l.slice(2).split(' ')[0];
    } else if (l.startsWith('a=mid:') && kind) {
      sections.push(`${kind}:${l.slice(6)}`);
      kind = null;
    }
  }
  if (kind) {
    sections.push(`${kind}:?`);
  }
  const bundle = lines.find(l => l.startsWith('a=group:BUNDLE'))?.slice(15) ?? 'none';
  const ufrags = lines.filter(l => l.startsWith('a=ice-ufrag:')).length;
  const lite = lines.some(l => l.startsWith('a=ice-lite')) ? ' · lite' : '';
  const inline = lines.filter(l => l.startsWith('a=candidate:')).length;
  return `${sections.join(' ')} · bundle ${bundle || '-'} · ufrag ${ufrags} · inline ${inline}${lite}`;
}

function summarizeAgent(entries: Record<string, unknown>[], pc: PeerConnection): string {
  const count = (type: string) => entries.filter(e => e.type === type).length;
  const pairs = entries.filter(e => e.type === 'candidate-pair');
  const states = pairs.reduce<Record<string, number>>((acc, e) => {
    const k = String(e.state ?? '?');
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
  const st = Object.entries(states)
    .map(([k, n]) => `${k} ${n}`)
    .join(', ');
  return [
    `conn ${pc.connectionState ?? '?'}`,
    `sig ${pc.signalingState ?? '?'}`,
    `local ${count('local-candidate')}`,
    `remote ${count('remote-candidate')}`,
    `pairs ${pairs.length}${st ? ` (${st})` : ''}`,
  ].join(' · ');
}

function candidateType(candidate: string): string {
  return / typ ([a-z]+)/.exec(candidate)?.[1] ?? '?';
}

function emptyTally(): CandidateTally {
  return {
    rx: {},
    rxRejected: 0,
    rxAdded: 0,
    rxError: null,
    tx: {},
    gathering: null,
    firstRx: null,
    offer: null,
    agent: null,
    answer: null,
  };
}

/** The offer's `a=mid` values in m-section order (`null` for a section without one). */
export function offerMids(sdp: string): (string | null)[] {
  const mids: (string | null)[] = [];
  for (const line of sdp.split(/\r?\n/)) {
    if (line.startsWith('m=')) {
      mids.push(null);
    } else if (line.startsWith('a=mid:') && mids.length > 0) {
      mids[mids.length - 1] = line.slice(6).trim();
    }
  }
  return mids;
}

/**
 * A camera candidate as the browser can attach it. Cameras send `sdpMid: null` with
 * only `sdpMLineIndex` — legal, but some Chromium builds (Yandex Browser on Android,
 * 2026-10-01) then accept the candidate and never hand it to the ICE agent: no error,
 * zero remote candidates, ICE parked in `new`. Naming the mid from the offer removes
 * the guess.
 */
export function withMid(c: CandidateInit, mids: (string | null)[]): CandidateInit {
  if (c.sdpMid) {
    return c;
  }
  const idx = typeof c.sdpMLineIndex === 'number' ? c.sdpMLineIndex : 0;
  const mid = mids[idx] ?? null;
  return mid === null ? c : {...c, sdpMid: mid, sdpMLineIndex: idx};
}

export type StartOptions = {
  /**
   * Сменить сессию под идущей картинкой: старая остаётся на экране, пока новая не
   * пришлёт свою. Для плановой смены токена и для восстановления после обрыва.
   */
  seamless?: boolean;
};

/** Сколько ждать, пока ICE сам выйдет из `disconnected`. */
const ICE_LOST_MS = 8_000;
/** Сколько кадры могут не идти, прежде чем картинка считается замёрзшей. */
const STALL_MS = 12_000;

const IDLE: LiveState = {
  phase: 'idle',
  stream: null,
  errorKey: null,
  errorCode: null,
  route: null,
  video: null,
  ice: null,
  tally: null,
};

/**
 * Signalling code → what to tell the user.
 *
 * The one that must never be got wrong is `403` vs `480`: `403` is about YOUR TOKEN
 * (wrong box, wrong profile, or no profile at all), `480` is about the TARGET, and it
 * deliberately conflates "offline", "belongs to another company" and "you called
 * yourself" so that the existence of other tenants' devices does not leak. Showing
 * `480` as "no access" sends an operator to demand permissions for a camera that is
 * merely switched off.
 */
function errorKeyFor(code: number): string {
  switch (code) {
    case SIGNALING_CODES.badRequest:
      return 'live.error.badRequest';
    case SIGNALING_CODES.unauthorized:
      return 'live.error.unauthorized';
    case SIGNALING_CODES.forbidden:
      return 'live.error.forbidden';
    case SIGNALING_CODES.noSession:
    case SIGNALING_CODES.expired:
      return 'live.error.sessionGone';
    case SIGNALING_CODES.peerTimeout:
      return 'live.error.peerTimeout';
    case SIGNALING_CODES.offline:
      return 'live.error.offline';
    case SIGNALING_CODES.overloaded:
      return 'live.error.overloaded';
    default:
      return 'live.error.unknown';
  }
}

/**
 * Which transport ICE settled on, read off the succeeded candidate pair.
 *
 * The server cannot see this: once ICE completes it is out of the media path
 * entirely, which is why it has to be told (§4.6). `relay` means the media is going
 * through TURN — the expensive path, and the number that decides whether the TURN
 * deployment is sized right.
 */
/** The route for the UI plus the raw local candidate type the server's metric wants
 *  (`host`/`srflx`/`prflx`/`relay`; `unknown` when the stats do not say). */
async function readPair(
  pc: PeerConnection,
): Promise<{route: IceOutcome; pairType: string}> {
  try {
    const stats = (await pc.getStats()) as Map<string, Record<string, unknown>>;
    const entries: Record<string, unknown>[] = [];
    stats.forEach?.(value => entries.push(value));

    const pair = entries.find(
      s => s.type === 'candidate-pair' && (s.state === 'succeeded' || s.nominated),
    );
    if (!pair) {
      return {route: 'direct', pairType: 'unknown'};
    }
    const local = entries.find(s => s.id === pair.localCandidateId);
    const type = typeof local?.candidateType === 'string' ? local.candidateType : 'unknown';
    return {route: type === 'relay' ? 'relay' : 'direct', pairType: type};
  } catch {
    // Stats are a diagnostic, never a gate: a browser that reports them in a shape
    // this does not expect must not turn a working picture into an error.
    return {route: 'direct', pairType: 'unknown'};
  }
}

export type UseLiveStreamOptions = {
  companyId: string;
  camera: StreamCamera | null;
  /** Profile token from the camera's own list — NOT its name. */
  profile: string | null;
  /**
   * Force media through TURN. Worth exposing rather than hard-coding `all`: relay is
   * the only path that works behind a strict NAT, and it breaks independently of the
   * direct one, so it needs to be testable without reproducing the NAT (§4.5).
   */
  relayOnly?: boolean;
};

export function useLiveStream({
  companyId,
  camera,
  profile,
  relayOnly = false,
}: UseLiveStreamOptions) {
  const [state, setState] = useState<LiveState>(IDLE);

  // Everything the teardown needs, kept out of state: none of it should re-render.
  const socketRef = useRef<SignalingSocket | null>(null);
  const pcRef = useRef<PeerConnection | null>(null);
  const extendRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const renewRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iceLostRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Последнее число показанных кадров и когда оно менялось — сторож заморозки. */
  const framesRef = useRef({value: -1, at: 0});
  /** Свежий `start` для таймеров, переживших рендер, в котором их завели. */
  const startRef = useRef<(options?: StartOptions) => Promise<void>>(async () => {});
  const phaseRef = useRef<LivePhase>('idle');
  phaseRef.current = state.phase;
  const pendingIce = useRef<CandidateInit[]>([]);
  const remoteReady = useRef(false);
  const tally = useRef<CandidateTally>(emptyTally());
  const mids = useRef<(string | null)[]>([]);
  // Guards every async continuation below. A user who taps play, stops, and plays
  // again must not have the first attempt's `invite` land on the second attempt's
  // peer connection — an id that only ever moves forward is the cheapest way to say
  // "this reply belongs to a run that no longer exists".
  const runId = useRef(0);

  const teardown = useCallback(() => {
    runId.current += 1;
    for (const timer of [extendRef, renewRef, iceLostRef]) {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    }
    socketRef.current?.close();
    socketRef.current = null;
    try {
      pcRef.current?.close();
    } catch {
      // A connection already closed by the far end. Nothing to release.
    }
    pcRef.current = null;
    // `close()` своей стороной гасит каналы без события — пульту надо сказать.
    refreshPtzChannels();
    pendingIce.current = [];
    remoteReady.current = false;
    tally.current = emptyTally();
    mids.current = [];
  }, []);

  const stop = useCallback(() => {
    teardown();
    setState(IDLE);
  }, [teardown]);

  const start = useCallback(async (options: StartOptions = {}) => {
    if (!camera || !profile) {
      return;
    }
    if (!WEBRTC_AVAILABLE) {
      setState({...IDLE, phase: 'error', errorKey: 'live.error.unsupported'});
      return;
    }
    const url = streamSocketUrl();
    if (!url) {
      setState({...IDLE, phase: 'error', errorKey: 'live.error.notConfigured'});
      return;
    }

    // Бесшовно — только поверх идущей картинки: она остаётся на экране (последним
    // кадром), пока новая сессия не пришлёт свою, и окно не мигает чёрным.
    const seamless = options.seamless === true && phaseRef.current === 'playing';
    teardown();
    const run = runId.current;
    const alive = () => runId.current === run;
    framesRef.current = {value: -1, at: Date.now()};
    /** Ход к картинке — в фазу, но не поверх показываемой при бесшовной смене. */
    const toPhase = (phase: LivePhase) => {
      if (!seamless) {
        setState(s => ({...s, phase}));
      }
    };
    /** Новая сессия поверх этой — когда эта вот-вот (или уже) умрёт. */
    const renew = () => {
      if (alive()) {
        void startRef.current({seamless: true});
      }
    };
    if (!seamless) {
      setState({...IDLE, phase: 'token'});
    }

    try {
      const minted = await mintStreamToken(companyId, camera.deviceId);
      if (!alive()) {
        return;
      }

      toPhase('socket');

      // One malformed candidate is not fatal — ICE succeeds on any pair — so a refusal
      // is counted for the diagnostics line and otherwise dropped.
      const addRemote = async (pc: PeerConnection, c: CandidateInit) => {
        try {
          await pc.addIceCandidate(iceCandidate(withMid(c, mids.current)));
          tally.current.rxAdded += 1;
        } catch (e) {
          tally.current.rxRejected += 1;
          tally.current.rxError ??= e instanceof Error ? e.message : String(e);
        }
      };

      const socket = new SignalingSocket(url, minted.token, {
        onInvite: async offer => {
          const pc = pcRef.current;
          if (!pc || !alive()) {
            throw new SignalingError(SIGNALING_CODES.noSession, 'no active session');
          }
          tally.current.offer = `${summarizeOffer(offer.sdp)} · ${summarizeVideoSection(offer.sdp)}`;
          mids.current = offerMids(offer.sdp);
          await pc.setRemoteDescription(
            sessionDescription('offer', constrainBaselineH264(offer.sdp)),
          );

          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          tally.current.answer = summarizeVideoSection(pc.localDescription?.sdp ?? answer.sdp);

          // Now, with BOTH descriptions set, the candidates that arrived first can be
          // applied. The spec only needs the remote one, but a Chromium build that
          // drops candidates added between the two is real (see `withMid`), and
          // waiting one more await costs nothing.
          remoteReady.current = true;
          for (const c of pendingIce.current) {
            await addRemote(pc, c);
          }
          pendingIce.current = [];
          if (alive() && !seamless) {
            // Not a plain `ice`: the track may already have arrived during
            // setRemoteDescription above, and that picture must stay (see phase.ts).
            setState(s => ({...s, phase: phaseAfterAnswer(s.phase)}));
          }
          return pc.localDescription?.sdp ?? answer.sdp;
        },
        onTrickle: candidates => {
          const pc = pcRef.current;
          if (!pc) {
            return;
          }
          for (const c of candidates) {
            const typ = candidateType(c.candidate);
            tally.current.rx[typ] = (tally.current.rx[typ] ?? 0) + 1;
            if (!tally.current.firstRx) {
              tally.current.firstRx = `${c.candidate} | mid ${c.sdpMid ?? 'null'} | idx ${
                c.sdpMLineIndex ?? 'null'
              }`;
            }
            if (remoteReady.current) {
              void addRemote(pc, c);
            } else {
              pendingIce.current.push(c);
            }
          }
        },
        onClosed: expected => {
          if (expected || !alive()) {
            return;
          }
          // The socket dies WITHOUT a close code both when the token reaches its exp
          // and when the network drops. With it goes the session: nobody is left to
          // `extend` it, and the camera stops sending a minute later. That was the
          // picture that froze after a few minutes until a reload (owner, 2026-10-22) —
          // the frame stayed, the phase stayed `playing`, and nothing retried. A
          // picture on screen is now carried over to a fresh session instead.
          if (phaseRef.current === 'playing') {
            renew();
            return;
          }
          setState({...IDLE, phase: 'error', errorKey: 'live.error.sessionGone'});
        },
      });
      socketRef.current = socket;
      await socket.open();
      if (!alive()) {
        return;
      }

      const session = await socket.connect(minted.peer, profile);
      if (!alive()) {
        return;
      }

      const iceStartedAt = Date.now();
      const pc = createPeerConnection(session.iceServers, relayOnly ? 'relay' : 'all');
      pcRef.current = pc;

      pc.onicecandidate = event => {
        const c = event.candidate as CandidateInit | null;
        if (c?.candidate) {
          const typ = candidateType(c.candidate);
          tally.current.tx[typ] = (tally.current.tx[typ] ?? 0) + 1;
          socket.trickle(session.sessionId, c);
        }
      };
      // Поворотная камера регистратора приходит с каналом `ptz` в том же оффере —
      // пульт ведёт её по нему напрямую, без очереди команд облака.
      const ptzFor = camera.deviceId;
      pc.ondatachannel = event => {
        if (event.channel.label === PTZ_CHANNEL_LABEL) {
          attachPtzChannel(ptzFor, event.channel);
        }
      };
      pc.ontrack = event => {
        const stream =
          (event.streams?.[0] as RemoteStream | undefined) ??
          streamFromTrack(event.track) ??
          undefined;
        if (stream && alive()) {
          setState(s => ({...s, phase: 'playing', stream}));
        }
      };
      pc.oniceconnectionstatechange = () => {
        const ice = pc.iceConnectionState;
        if (!alive()) {
          return;
        }
        setState(s => ({...s, ice}));
        if (ice === 'connected' || ice === 'completed') {
          void readPair(pc).then(({route, pairType}) => {
            if (!alive()) {
              return;
            }
            setState(s => ({...s, route}));
            if (session.sessionId) {
              reportIceOutcome(
                session.sessionId,
                pairType,
                Date.now() - iceStartedAt,
                minted.token,
              );
            }
          });
        } else if (ice === 'failed') {
          // Not reported: the server's metric counts established pairs only, and a
          // failure has no pair type to report.
          setState({...IDLE, phase: 'error', errorKey: 'live.error.iceFailed'});
        }
        // `disconnected` бывает и мигом сети, который ICE переживает сам, — поэтому
        // не сразу. Но и не вечно: Chrome может держать его минутами, не доходя до
        // `failed`, и всё это время окно показывает застывший кадр как живой.
        if (iceLostRef.current) {
          clearTimeout(iceLostRef.current);
          iceLostRef.current = null;
        }
        if (ice === 'disconnected') {
          iceLostRef.current = setTimeout(() => {
            if (alive() && pc.iceConnectionState !== 'connected' && pc.iceConnectionState !== 'completed') {
              teardown();
              setState({...IDLE, phase: 'error', errorKey: 'live.error.iceFailed'});
            }
          }, ICE_LOST_MS);
        }
      };

      toPhase('offer');

      // Renew at 70% of the term: late enough not to chatter, early enough that one
      // lost renewal still leaves room for the next before the session dies.
      const scheduleExtend = (seconds: number) => {
        if (extendRef.current) {
          clearTimeout(extendRef.current);
        }
        extendRef.current = setTimeout(() => {
          if (!alive()) {
            return;
          }
          socket
            .extend(session.sessionId)
            .then(next => alive() && scheduleExtend(next))
            // Не продлилась — значит, умирает: новая сессия, пока картинка ещё идёт.
            .catch(renew);
        }, Math.max(15, seconds * 0.7) * 1000);
      };
      scheduleExtend(session.expiryTimeSeconds);

      // Сокет живёт ровно до `exp` своего токена (cameras/live-video.md §4.5), и
      // `extend` его не продлевает: тот же токен. Поэтому до истечения — новая сессия
      // с новым токеном, поверх идущей картинки. Запас — пятая часть срока, но не
      // меньше 30 с: выпуск токена и ICE занимают секунды.
      const term = minted.expiresInSeconds;
      if (Number.isFinite(term) && term > 0) {
        const lead = Math.max(30, term * 0.2);
        renewRef.current = setTimeout(renew, Math.max(15, term - lead) * 1000);
      }
    } catch (e) {
      if (!alive()) {
        return;
      }
      teardown();
      const code = e instanceof SignalingError ? e.code : null;
      setState({
        ...IDLE,
        phase: 'error',
        errorCode: code,
        errorKey: code === null ? 'live.error.unknown' : errorKeyFor(code),
      });
    }
  }, [camera, companyId, profile, relayOnly, teardown]);
  startRef.current = start;

  // From the moment a peer connection exists, read what the media is doing every two
  // seconds. A player stuck on "connecting" or a blank rectangle says nothing; these
  // counters say whether packets, frames or decoding is where it stops.
  const polling = state.phase === 'ice' || state.phase === 'playing';
  useEffect(() => {
    if (!polling) {
      return;
    }
    const timer = setInterval(() => {
      const pc = pcRef.current;
      if (!pc) {
        return;
      }
      pc.getStats()
        .then(raw => {
          const entries: Record<string, unknown>[] = [];
          (raw as Map<string, Record<string, unknown>>).forEach?.(v => entries.push(v));
          const video = summarizeVideoStats(entries);
          // Сторож заморозки: ICE «connected», а кадров нет — камера перестала слать
          // (перезагрузилась, регистратор отпустил поток). Без него окно часами
          // показывало бы последний кадр как прямой эфир.
          const now = Date.now();
          const frames = video?.framesDecoded ?? 0;
          if (frames !== framesRef.current.value) {
            framesRef.current = {value: frames, at: now};
          } else if (
            phaseRef.current === 'playing' &&
            now - framesRef.current.at > STALL_MS
          ) {
            framesRef.current = {value: frames, at: now};
            void startRef.current({seamless: true});
            return;
          }
          const t = tally.current;
          t.gathering = pc.iceGatheringState ?? null;
          t.agent = summarizeAgent(entries, pc);
          const snapshot = {...t, rx: {...t.rx}, tx: {...t.tx}};
          setState(s =>
            s.phase === 'ice' || s.phase === 'playing'
              ? {...s, video, ice: pc.iceConnectionState, tally: snapshot}
              : s,
          );
        })
        .catch(() => {
          // Diagnostics only; a browser without stats keeps playing.
        });
    }, 2000);
    return () => clearInterval(timer);
  }, [polling]);

  // Tearing down on unmount is not housekeeping here: a peer connection left open
  // keeps pulling video over the site's uplink after the screen is gone.
  useEffect(() => teardown, [teardown]);

  return {state, start, stop};
}
